import React, { useState, useEffect } from 'react';
import classes from "../../form/form.module.css"
import InputField from "../../../containers/inputField/inputField";
import { sixYardAxios } from '../../../../sixYardAxios';
import qs from "qs";
import PromptMessage from '../../../containers/promptMessage/promptMessage';

const PlayerForm = (props) => {
    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const [message, setMessage] = useState(false);
    const [error, setError] = useState(false);
    const [currentLang, setCurrentLang] = useState(document.documentElement.lang || 'en');
    
    // Listen for language changes
    useEffect(() => {
        const checkLanguage = () => {
            const htmlLang = document.documentElement.lang;
            if (htmlLang !== currentLang) {
                setCurrentLang(htmlLang);
                updateFormLabels(htmlLang);
            }
        };
        
        // Check immediately and also set up an interval to poll for changes
        checkLanguage();
        const intervalId = setInterval(checkLanguage, 500);
        
        // Clean up interval on component unmount
        return () => clearInterval(intervalId);
    }, [currentLang]);
    
    // Utility function to generate a range of years from 1900 to the current year
    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = 1900; year <= currentYear; year++) {
            years.push({ value: year, displayValue: year });
        }
        return years;
    };
    
    // Function to get localized labels
    const getLocalizedText = (enText, arText) => {
        return currentLang === 'en' ? enText : arText;
    };
    
    const [controls, setControls] = useState({
        playerName: {
            elementType: "input",
            elementConfig: {
                type: "text",
                placeholder: "Enter name|أدخل الاسم",
            },
            value: "",
            validation: {
                required: true,
            },
            label: getLocalizedText("Player's Full Name", "الاسم الكامل للاعب"),
            valid: false,
            touched: false
        },
        gender: {
            elementType: "select",
            elementConfig: {
                type: "select",
                placeholder: "Select gender|اختر الجنس",
                options: [
                    { value: 'male', displayValue: getLocalizedText('Male', 'ذكر') }, 
                    { value: 'female', displayValue: getLocalizedText('Female', 'أنثى') }
                ]
            },
            value: "",
            validation: {
                required: true,
            },
            label: getLocalizedText("Gender", "الجنس"),
            valid: false,
            touched: false
        },
        yearOfBirth: {
            elementType: "select",
            elementConfig: {
                type: "select",
                placeholder: "Select year|اختر السنة",
                options: generateYearOptions()
            },
            value: "",
            validation: {
                required: true,
            },
            label: getLocalizedText("Year Of Birth", "سنة الميلاد"),
            valid: false,
            touched: false
        },
        email: {
            elementType: "input",
            elementConfig: {
                type: "email",
                placeholder: "Enter email|أدخل البريد الإلكتروني",
            },
            value: "",
            label: getLocalizedText("Email", "البريد الإلكتروني"),
            validation: {
                required: true,
                isEmail: true
            },
            valid: false,
            touched: false
        },
        phone: {
            elementType: "input",
            elementConfig: {
                type: "phone",
                placeholder: "07xxxxxxxx",
                options: []
            },
            value: "",
            valueName: "phone_number",
            validation: {
                required: true,
                isNumeric: true
            },
            label: getLocalizedText("Phone", "رقم الهاتف"),
            valid: false,
            touched: false
        },
    });
    
    // Function to update form labels based on language
    const updateFormLabels = (lang) => {
        const updatedControls = { ...controls };
        
        updatedControls.playerName.label = lang === 'en' ? "Player's Full Name" : "الاسم الكامل للاعب";
        updatedControls.playerName.elementConfig.placeholder = "Enter name|أدخل الاسم";
        
        updatedControls.gender.label = lang === 'en' ? "Gender" : "الجنس";
        updatedControls.gender.elementConfig.placeholder = "Select gender|اختر الجنس";
        updatedControls.gender.elementConfig.options = [
            { value: 'male', displayValue: lang === 'en' ? 'Male' : 'ذكر' }, 
            { value: 'female', displayValue: lang === 'en' ? 'Female' : 'أنثى' }
        ];
        
        updatedControls.yearOfBirth.label = lang === 'en' ? "Year Of Birth" : "سنة الميلاد";
        updatedControls.yearOfBirth.elementConfig.placeholder = "Select year|اختر السنة";
        
        updatedControls.email.label = lang === 'en' ? "Email" : "البريد الإلكتروني";
        updatedControls.email.elementConfig.placeholder = "Enter email|أدخل البريد الإلكتروني";
        
        updatedControls.phone.label = lang === 'en' ? "Phone" : "رقم الهاتف";
        updatedControls.phone.elementConfig.placeholder = "07xxxxxxxx";
        
        setControls(updatedControls);
    };

    const formElementsArray = [];
    for (let key in controls) {
        formElementsArray.push({
            id: key,
            config: controls[key]
        });
    }
    const form = formElementsArray.map((formElement, i) => (
        <div key={i} className={classes.FormGroup}>
            <InputField
                key={formElement.id}
                elementType={formElement.config.elementType}
                elementConfig={formElement.config.elementConfig}
                value={formElement.config.value}
                invalid={!formElement.config.valid}
                shouldValidate={formElement.config.validation}
                touched={formElement.config.touched}
                withRemove={false}
                label={formElement.config.label}
                changed={event => inputChangedHandler(event, formElement.id)}
            />
        </div>
    ));
    const checkValidity = ((value, rules) => {
        let isValid = true;
        if (!rules) {
            return true;
        }

        if (rules.required) {
            value = typeof value === 'string' ? value.trim() : value;
            isValid = value !== "" && isValid;
        }

        if (rules.isEmail) {
            const pattern = /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/;
            isValid = pattern.test(value) && isValid;
        }

        if (rules.isNumeric) {
            const pattern = /^\d+$/;
            isValid = pattern.test(value) && isValid;
        }

        return isValid;
    });
    
    const inputChangedHandler = (event, controlName) => {
        const updatedControls = {
            ...controls,
            [controlName]: {
                ...controls[controlName],
                value: controlName === 'phone' ? event : event.target.value,
                valid: controlName === 'phone' ? checkValidity(event, controls[controlName].validation) : checkValidity(event.target.value, controls[controlName].validation),
                touched: true
            }
        };
        setControls(updatedControls);
    };
    
    const submitHandler = e => {
        e.preventDefault();
        if (!props.isAuthenticated) props.setShowLoginForm(true)
        else {
            sixYardAxios({
                method: "post",
                url: "/tournaments/register-player",
                data: qs.stringify({
                    "tournament_id": props.id,
                    "name": controls.playerName.value,
                    "gender": controls.gender.value,
                    "birth_year": controls.yearOfBirth.value,
                    "email": controls.email.value,
                    "phone": controls.phone.value.dialCode + controls.phone.value.number,
                })
            })
                .then(response => {
                    setMessage(response.data.data.message);
                    clearControls();
                })
                .catch(error => {
                    setError(error.response.data.message);
                });
        }
    };
    
    const clearControls = () => {
        const updatedControls = {
            ...controls
        };
        for (let key in controls) {
            updatedControls[key] = {
                ...controls[key],
                value: "",
                valid: false,
                touched: false
            };
        }
        setControls(updatedControls);
    };
    
    return (
        <div className={classes.Container}>
            <form className={classes.FormContainer} onSubmit={(e) => submitHandler(e)}>
                <p className={classes.Description}>
                    {currentLang === 'en' 
                        ? "Fill up the form and submit it and we will contact you shortly"
                        : "املأ النموذج وأرسله وسنتواصل معك قريباً"}
                </p>
                
                {/* Display player name, gender, year of birth, and email fields */}
                <div className={classes.FormGroup}>
                    <InputField
                        key="playerName"
                        elementType={controls.playerName.elementType}
                        elementConfig={controls.playerName.elementConfig}
                        value={controls.playerName.value}
                        invalid={!controls.playerName.valid}
                        shouldValidate={controls.playerName.validation}
                        touched={controls.playerName.touched}
                        withRemove={false}
                        label={controls.playerName.label}
                        changed={event => inputChangedHandler(event, "playerName")}
                    />
                </div>
                
                <div className={classes.FormGroup}>
                    <InputField
                        key="gender"
                        elementType={controls.gender.elementType}
                        elementConfig={controls.gender.elementConfig}
                        value={controls.gender.value}
                        invalid={!controls.gender.valid}
                        shouldValidate={controls.gender.validation}
                        touched={controls.gender.touched}
                        withRemove={false}
                        label={controls.gender.label}
                        changed={event => inputChangedHandler(event, "gender")}
                    />
                </div>
                
                <div className={classes.FormGroup}>
                    <InputField
                        key="yearOfBirth"
                        elementType={controls.yearOfBirth.elementType}
                        elementConfig={controls.yearOfBirth.elementConfig}
                        value={controls.yearOfBirth.value}
                        invalid={!controls.yearOfBirth.valid}
                        shouldValidate={controls.yearOfBirth.validation}
                        touched={controls.yearOfBirth.touched}
                        withRemove={false}
                        label={controls.yearOfBirth.label}
                        changed={event => inputChangedHandler(event, "yearOfBirth")}
                    />
                </div>
                
                <div className={classes.FormGroup}>
                    <InputField
                        key="email"
                        elementType={controls.email.elementType}
                        elementConfig={controls.email.elementConfig}
                        value={controls.email.value}
                        invalid={!controls.email.valid}
                        shouldValidate={controls.email.validation}
                        touched={controls.email.touched}
                        withRemove={false}
                        label={controls.email.label}
                        changed={event => inputChangedHandler(event, "email")}
                    />
                </div>
                
                {/* Phone field with custom styling to ensure it works in all languages */}
                <div className={classes.FormGroup} style={{ direction: 'ltr' }}>
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        textAlign: currentLang === 'en' ? 'left' : 'right',
                        direction: currentLang === 'en' ? 'ltr' : 'rtl',
                        width: '100%',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}>
                        {controls.phone.label}
                        {controls.phone.validation && controls.phone.validation.required && 
                            <span style={{ color: '#c52542' }}>*</span>
                        }
                    </label>
                    <InputField
                        key="phone"
                        elementType={controls.phone.elementType}
                        elementConfig={controls.phone.elementConfig}
                        value={controls.phone.value}
                        invalid={!controls.phone.valid}
                        shouldValidate={controls.phone.validation}
                        touched={controls.phone.touched}
                        withRemove={false}
                        changed={event => inputChangedHandler(event, "phone")}
                    />
                </div>
                
                <p className={classes.Prompt}>
                    {currentLang === 'en' 
                        ? "Please read and agree to our "
                        : "يرجى قراءة والموافقة على "}
                    <a href="/privacy-policy" target='_blank' className={classes.Link}>
                        {currentLang === 'en' 
                            ? "Privacy Policy "
                            : "سياسة الخصوصية "}
                    </a>
                    {currentLang === 'en' ? "and " : "و "}
                    <a href="/terms-and-conditions" target='_blank' className={classes.Link}>
                        {currentLang === 'en' ? "Terms" : "الشروط"}
                    </a>
                </p>
                <div className={classes.CheckBox}>
                    <input type="checkbox" onClick={() => setIsTermsChecked(!isTermsChecked)} />
                    <label className={classes.Label}>
                        {currentLang === 'en' 
                            ? "I have read and agreed to terms and conditions and privacy policy"
                            : "لقد قرأت ووافقت على الشروط والأحكام وسياسة الخصوصية"}
                    </label>
                </div>
                <div>
                    <button type="submit" 
                        className={[classes.submitButton, !isTermsChecked && classes.Disabled].join(" ")}
                        disabled={!isTermsChecked}
                    >
                        {currentLang === 'en' ? "SUBMIT" : "إرسال"}
                        <img
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/b4d5bcaf1ebb966f9c466b88bc4db272a664766c03afdc1eeb10bad706c68a4a?placeholderIfAbsent=true&apiKey=55c20a0273674f7297318d8ed49e9ee9"
                            alt=""
                            className={classes.submitIcon}
                        />
                    </button>
                </div>
            </form>
            {message && <PromptMessage 
                successMessage 
                message={message || (currentLang === 'en' 
                    ? "Your order was submitted successfully. We will reach you soon"
                    : "تم تقديم طلبك بنجاح. سنتواصل معك قريباً")} 
                status={currentLang === 'en' ? 'CONGRATULATIONS' : 'تهانينا'} 
                button={currentLang === 'en' ? 'CLOSE' : 'إغلاق'} 
                click={() => setMessage(false)} 
            />}
            {error && <PromptMessage 
                message={error} 
                status={currentLang === 'en' ? 'Oops' : 'عفواً'} 
                button={currentLang === 'en' ? 'CLOSE' : 'إغلاق'} 
                click={() => setError(false)} 
            />}
        </div>
    )
}
export default PlayerForm;